import type {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import {normalizeFilterRules} from "../../../utils/filterUrlState";
import type {ServerTableUrlFilterAlias, ServerTableUrlStateOptions} from "./types";

const FILTER_STATE_VERSION = 1;

const URL_KEYS = {
    search: 'q',
    filters: 'filters'
} as const;
const LEGACY_URL_KEYS = ['page', 'perPage', 'sort'] as const;

/** Stable selection criteria represented in a shareable URL. */
export interface ServerTableUrlQueryState {
    searchQuery: string
    filterRules: FilterRule[]
}

export interface ServerTableUrlStateContext {
    urlState: ServerTableUrlStateOptions
}

const prefixedParamName = (options: ServerTableUrlStateOptions, key: string) => (
    options.paramPrefix ? `${options.paramPrefix}.${key}` : key
);
const paramName = (options: ServerTableUrlStateOptions, key: keyof typeof URL_KEYS) => (
    prefixedParamName(options, URL_KEYS[key])
);

const parseModernFilterRules = (raw: string) => {
    try {
        const parsed = JSON.parse(raw) as {version?: unknown; rules?: unknown};
        return parsed?.version === FILTER_STATE_VERSION ? normalizeFilterRules(parsed.rules) : [];
    } catch {
        return [];
    }
};

const aliasedFilterRules = (
    searchParams: URLSearchParams,
    options: ServerTableUrlStateOptions
) => (options.filterAliases ?? []).flatMap(alias => {
    const values = searchParams.getAll(alias.queryParam).filter(Boolean);
    return values.length ? [{
        field: alias.field,
        operator: alias.operator,
        textValues: values
    } satisfies FilterRule] : [];
});

/**
 * An alias query parameter can only carry plain text values. A rule with flags or
 * non-text values must stay in the versioned `filters` JSON, or those parts would be
 * silently dropped on the next page load.
 */
const isAliasRepresentable = (rule: FilterRule, alias: ServerTableUrlFilterAlias) => (
    rule.field === alias.field
    && rule.operator === alias.operator
    && !!rule.textValues?.length
    && rule.textValues.every(value => !!value)
    && !rule.longValues?.length
    && !rule.doubleValues?.length
    && !rule.localDateValues?.length
    && !rule.timestampValues?.length
    && !rule.isExclusive
    && !rule.isDeactivated
);

const parseFilterRules = (
    searchParams: URLSearchParams,
    options: ServerTableUrlStateOptions
) => {
    const aliasRules = aliasedFilterRules(searchParams, options);
    const modernRaw = searchParams.get(paramName(options, 'filters'));

    // Aliased rules come first so re-serializing keeps them in the readable parameters.
    // Rules from the JSON parameter are kept as-is: the writer guarantees the two sets
    // are disjoint, so filtering one against the other would only ever lose data.
    return modernRaw === null
        ? aliasRules
        : [...aliasRules, ...parseModernFilterRules(modernRaw)];
};

export const parseServerTableUrlState = (
    searchParams: URLSearchParams,
    {urlState}: ServerTableUrlStateContext
): ServerTableUrlQueryState => ({
    searchQuery: searchParams.get(paramName(urlState, 'search')) ?? '',
    filterRules: parseFilterRules(searchParams, urlState)
});

const writeSearch = (
    searchParams: URLSearchParams,
    searchQuery: string,
    options: ServerTableUrlStateOptions
) => {
    const key = paramName(options, 'search');
    searchQuery ? searchParams.set(key, searchQuery) : searchParams.delete(key);
};

const writeFilters = (
    searchParams: URLSearchParams,
    filterRules: FilterRule[],
    options: ServerTableUrlStateOptions
) => {
    const normalizedRules = normalizeFilterRules(filterRules);
    const aliasEncodedIndexes = new Set<number>();

    // Each alias takes the first rule it can represent completely; every other rule —
    // including a second rule on the same field — goes into the JSON parameter instead.
    for (const alias of options.filterAliases ?? []) {
        searchParams.delete(alias.queryParam);
        const ruleIndex = normalizedRules.findIndex((rule, index) => (
            !aliasEncodedIndexes.has(index) && isAliasRepresentable(rule, alias)
        ));
        if (ruleIndex < 0) continue;

        aliasEncodedIndexes.add(ruleIndex);
        normalizedRules[ruleIndex].textValues
            ?.filter((value): value is string => !!value)
            .forEach(value => searchParams.append(alias.queryParam, value));
    }

    const genericRules = normalizedRules.filter((_, index) => !aliasEncodedIndexes.has(index));
    const filterParam = paramName(options, 'filters');
    genericRules.length
        ? searchParams.set(filterParam, JSON.stringify({version: FILTER_STATE_VERSION, rules: genericRules}))
        : searchParams.delete(filterParam);
};

/** Writes only durable selection criteria and removes obsolete table-view parameters. */
export const writeServerTableUrlState = (
    searchParams: URLSearchParams,
    state: ServerTableUrlQueryState,
    {urlState}: ServerTableUrlStateContext
) => {
    LEGACY_URL_KEYS.forEach(key => searchParams.delete(prefixedParamName(urlState, key)));
    writeSearch(searchParams, state.searchQuery, urlState);
    writeFilters(searchParams, state.filterRules, urlState);
};
